import { useState, useEffect, useRef } from 'react';

interface CalendarProps {
  /** Selected date in "YYYY-MM-DD" format */
  selectedDate: string;
  /** Callback with date in "YYYY-MM-DD" format */
  onSelect: (date: string) => void;
  onClose: () => void;
  /** Minimum selectable date in "YYYY-MM-DD" format */
  minDate?: string;
}

/** Parse "YYYY-MM-DD" string to get year, month, day */
function parseDateString(dateStr: string): { year: number; month: number; day: number } {
  const [year, month, day] = dateStr.split('-').map(Number);
  return { year, month: month - 1, day }; // month is 0-indexed for Date
}

/** Format date components to "YYYY-MM-DD" string */
function formatDateString(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, '0'); // month is 0-indexed
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

/** Get today's date as "YYYY-MM-DD" string */
function getTodayString(): string {
  const today = new Date();
  return formatDateString(today.getFullYear(), today.getMonth(), today.getDate());
}

export function Calendar({ selectedDate, onSelect, onClose, minDate = getTodayString() }: CalendarProps) {
  const selected = parseDateString(selectedDate);
  const [currentMonth, setCurrentMonth] = useState(
    new Date(selected.year, selected.month, 1)
  );
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal on Esc key press and handle focus trap
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        return;
      }

      // Focus trap: Tab and Shift+Tab
      if (event.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [tabindex="0"]'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement?.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement?.focus();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    // Focus first button when modal opens
    const firstButton = modalRef.current?.querySelector<HTMLElement>('button:not([disabled])');
    firstButton?.focus();

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, currentMonth]);

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  const previousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateClick = (day: number) => {
    const dateStr = formatDateString(currentMonth.getFullYear(), currentMonth.getMonth(), day);

    // Check if date is before minDate
    if (minDate && dateStr < minDate) {
      return;
    }

    onSelect(dateStr);
    onClose();
  };

  const isDateDisabled = (day: number): boolean => {
    const dateStr = formatDateString(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return Boolean(minDate && dateStr < minDate);
  };

  const isDateSelected = (day: number) => {
    return (
      day === selected.day &&
      currentMonth.getMonth() === selected.month &&
      currentMonth.getFullYear() === selected.year
    );
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isPreviousMonthDisabled = () => {
    if (!minDate) return false;
    const minParsed = parseDateString(minDate);
    const prevMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1);
    return prevMonth < new Date(minParsed.year, minParsed.month, 1);
  };

  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const emptyDays = Array.from({ length: firstDayOfMonth }, (_, i) => i);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Select date"
    >
      <div
        ref={modalRef}
        className="w-full max-w-md rounded-t-3xl bg-card-dark p-6 animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={previousMonth}
            disabled={isPreviousMonthDisabled()}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-background-dark text-text-primary-dark hover:bg-background-dark/80 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
          >
            <span className="material-symbols-outlined">chevron_left</span>
          </button>

          <h2 className="text-lg font-bold text-text-primary-dark">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h2>

          <button
            onClick={nextMonth}
            className="flex h-10 w-10 items-center justify-center rounded-full bg-background-dark text-text-primary-dark hover:bg-background-dark/80 transition-all"
          >
            <span className="material-symbols-outlined">chevron_right</span>
          </button>
        </div>

        {/* Weekday Headers */}
        <div className="mb-3 grid grid-cols-7 gap-2">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-text-secondary-dark">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-2">
          {/* Empty cells for days before the first of the month */}
          {emptyDays.map((i) => (
            <div key={`empty-${i}`} />
          ))}

          {/* Actual days */}
          {days.map((day) => {
            const disabled = isDateDisabled(day);
            const selected = isDateSelected(day);
            const today = isToday(day);

            return (
              <button
                key={day}
                onClick={() => !disabled && handleDateClick(day)}
                disabled={disabled}
                className={`
                  relative flex aspect-square w-full items-center justify-center rounded-xl text-sm font-semibold transition-all
                  ${disabled
                    ? 'cursor-not-allowed text-text-secondary-dark/30'
                    : selected
                    ? 'cursor-pointer bg-primary text-black! font-bold shadow-glow-primary'
                    : 'cursor-pointer text-text-primary-dark hover:bg-background-dark'
                  }
                  ${today && !selected
                    ? 'ring-2 ring-primary/50'
                    : ''
                  }
                `}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 rounded-xl bg-background-dark py-3 text-sm font-semibold text-text-primary-dark hover:bg-background-dark/80 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              onSelect(getTodayString());
              onClose();
            }}
            className="flex-1 rounded-xl bg-primary/10 py-3 text-sm font-semibold text-primary hover:bg-primary/20 transition-all"
          >
            Today
          </button>
        </div>
      </div>
    </div>
  );
}
